import React, { useEffect, useState, useRef, useCallback } from 'react';
import GameCard from "../gamecard/gameCard";
import AdSideBar from "../ad/adSideBar";
import Loader from "../Loader/loader";
import LineSeparator from "../bgMadness/line";
import CardGrid from "../gamecard/cardGrid";
import { useParams } from 'react-router-dom';
import AllGames from "../../data/gameData.json";
import AllCategories from "../../data/category.json";
import slugify from "../../utils/slugify";
import { Helmet } from 'react-helmet-async';


const GAMES_PER_PAGE = 24;

const Home = () => {
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filteredGames, setFilteredGames] = useState([]);
  const observerRef = useRef();
  const { category } = useParams();

  let categoryDetails = AllCategories.find(cat => slugify(cat.title) === category);

  const categoryName = categoryDetails?.title || ""
  const descriptionBlock = categoryDetails?.description || `Play free ${categoryName} games online at BrowserArena. No installs. Just click and play instantly.`;

  useEffect(() => {
    // Filter all games by category once
    let filtered = AllGames.data.filter(g => slugify(g.category) === category);
    if (filtered.length == 0) {
      const prefix = category.substring(0, 3);

      const matchedKey = Object.keys(AllGames).find(key => key.startsWith(prefix));
      if (matchedKey && AllGames[matchedKey]) {
        filtered = AllGames[matchedKey]
      } else {
        filtered = AllGames.data;
      }
    }

    setFilteredGames(filtered);
    setPage(1);
    setAllGames(filtered.slice(0, GAMES_PER_PAGE));
    setHasMore(filtered.length > GAMES_PER_PAGE);
    setLoading(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [category]);

  const loadMoreGames = () => {
    const startIndex = page * GAMES_PER_PAGE;
    const newItems = filteredGames.slice(startIndex, startIndex + GAMES_PER_PAGE);
    setAllGames(prev => [...prev, ...newItems]);
    setHasMore(startIndex + GAMES_PER_PAGE < filteredGames.length);
    setPage(prev => prev + 1);
  };

  const lastElementRef = useCallback((node) => {
    if (loading || !hasMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreGames();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, filteredGames, page]);

  return (
    <div className="relative min-h-screen bg-[#0d0d1a] text-[#00F0FF] px-4 sm:px-6 lg:px-10 py-8 overflow-hidden">
      {/* Background Lights */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a3e] via-[#12122b] to-[#0d0d1a] opacity-90 -z-10" />
      <div className="absolute top-10 left-10 w-72 h-72 bg-[#00F0FF] opacity-20 rounded-full blur-[100px] -z-10 animate-pulse" />
      <div className="absolute bottom-10 right-10 w-60 h-60 bg-[#FF007F] opacity-20 rounded-full blur-[80px] -z-10 animate-pulse" />

      <Helmet>
        <title>{`${categoryName} Games | Free Online ${categoryName} Games | BrowserArena`}</title>
        <meta name="description" content={`Enjoy the best ${categoryName} browser games online. No installs. Just click and play!`} />
        <link rel="canonical" href={`https://browserarena.com/games/${slugify(categoryName)}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": `${categoryName} Games`,
            "url": `https://browserarena.com/games/${slugify(categoryName)}`,
            "itemListElement": allGames.map((game, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "url": `https://browserarena.com/game/${game.slug}`
            }))
          })}
        </script>
      </Helmet>

      <div className="relative z-10 text-center mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold uppercase tracking-wide text-[#00F0FF]">
          🎮 {categoryName} Games
        </h1>
        <div className="mt-2 sm:mt-3 text-xs sm:text-sm md:text-base text-[#00F0FF]/80 max-w-2xl mx-auto px-2 sm:px-4">
          {descriptionBlock}
        </div>
      </div>
      <LineSeparator />
      <CardGrid allGames={allGames} />
      {/* Loader */}
      {
        hasMore && (
          <div ref={lastElementRef} className="flex justify-center mt-10">
            <Loader />
          </div>
        )
      }
    </div >
  );
};

export default Home;
